import { FC, ReactNode } from "react";
import { Box, createStyles, makeStyles, Typography } from "@material-ui/core";
import { get } from "lodash";

import { EssentialImage } from "components";
import theme from "theme";

import {
  NextStepBoxCheckboxBackgroundColor,
  NextStepBoxCheckboxColor,
  NextStepBoxCheckboxContainerBackgroundColor,
  NextStepBoxCircleSrcImageName,
  NextStepBoxIconTextTextSize,
  NextStepBoxIconTextValue,
} from "./constants";

export type NextStepStatus = "pending" | "failed" | "ok" | "empty";
export interface NextStepBoxProps {
  title: string;
  description: JSX.Element;
  status: NextStepStatus;
  children?: ReactNode;
}

const useStyles = makeStyles(() =>
  createStyles({
    nextStepBox: {
      border: `1px solid ${theme.custom.palette.gray.main}`,
      borderRadius: theme.shape.borderRadius,
      display: "flex",
      backgroundColor: theme.palette.background.default,
      minHeight: "180px",
      marginBottom: theme.spacing(3),
    },
    nextStepBoxTitle: {
      fontSize: "20px",
      fontWeight: theme.typography.fontWeightBold,
      marginBottom: theme.spacing(1.25),
    },
    nextStepBoxDescription: {
      marginBottom: theme.spacing(1.25),
      textAlign: "left",
    },
    nextStepBoxCheckboxContainer: {
      width: "50px",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: `0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0`,
      borderLeft: `1px solid ${theme.custom.palette.gray.main}`,
    },
    nextStepBoxCheckbox: {
      fontSize: "30px",
      fontWeight: theme.typography.fontWeightLight,
      borderRadius: "50%",
      width: "38px",
      height: "38px",
    },
    nextStepBoxIconText: {
      fontWeight: theme.typography.fontWeightBold,
      textAlign: "center",
      color: theme.palette.primary.contrastText,
      marginTop: theme.spacing(0.375),
    },
  }),
);

const NextStepBox: FC<NextStepBoxProps> = ({ title, description, status }) => {
  const classes = useStyles();

  const circleSrcImageName = get(NextStepBoxCircleSrcImageName, status);
  const circleSrcImage = `${
    import.meta.env.REACT_APP_STATIC_URL
  }/images/orderbook/${circleSrcImageName}.svg`;

  const checkboxContainerStyle = {
    backgroundColor: get(NextStepBoxCheckboxContainerBackgroundColor, status),
  };

  const checkboxStyle = {
    color: get(NextStepBoxCheckboxColor, status),
    backgroundColor: get(NextStepBoxCheckboxBackgroundColor, status),
  };

  const iconTextStyle = {
    fontSize: get(NextStepBoxIconTextTextSize, status),
  };

  return (
    <Box className={classes.nextStepBox}>
      <Box p={2} pr={3} flex={1}>
        <Typography className={classes.nextStepBoxTitle}>{title}</Typography>
        <Typography className={classes.nextStepBoxDescription}>
          {description}
        </Typography>
      </Box>
      <Box
        className={classes.nextStepBoxCheckboxContainer}
        style={checkboxContainerStyle}
      >
        <Box
          height="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Box alignSelf="center" textAlign="center">
            <EssentialImage>
              <img
                className={classes.nextStepBoxCheckbox}
                style={checkboxStyle}
                alt="loading"
                src={circleSrcImage}
                id={`${title}-${circleSrcImageName}`}
              />
            </EssentialImage>
            {status !== "empty" && (
              <Typography
                className={classes.nextStepBoxIconText}
                style={iconTextStyle}
              >
                {get(NextStepBoxIconTextValue, status)}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default NextStepBox;
